/**
 * useConnectDrag — the Ctrl+drag "draw a connection from one item to another" gesture, extracted
 * from Canvas.tsx's (OST) former inline connectMode state machine so it can be reused by any
 * canvas that places id-keyed items, regardless of how those items are actually rendered (SVG
 * nodes for OST's Canvas.tsx, HTML overlay divs for the Plan page's TimelineCanvas/GridCanvas).
 *
 * This hook is deliberately content-agnostic: it only tracks a source id, a hovered target id,
 * and canvas-space start/current points, and fires `onFinish` on mouseup with whatever those were
 * last set to. It does NOT decide what makes a target "valid" (excluding the source itself,
 * excluding an existing/duplicate connection, excluding a parent-child pair, etc.) — that's the
 * caller's business logic, applied inside `onFinish` (see Canvas.tsx and PlanPage.tsx for two
 * different rule sets built on the same hook).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface ConnectDragPoint {
    x: number;
    y: number;
}

export interface UseConnectDragOptions {
    /** Converts a screen point (clientX/clientY) to canvas space, matching whatever pan/zoom the
     * host canvas is currently at — e.g. OST Canvas.tsx's own screenToCanvas, or the Plan page's
     * TimelineCanvasHandle.screenToCanvas. Called once (from `start`) and again on every
     * mousemove while the drag is active. */
    screenToCanvas: (clientX: number, clientY: number) => ConnectDragPoint | null;
    /** Fired on mouseup while a drag is active, then the hook resets. `targetId` is whatever id
     * was last passed to `setHoverTarget` — null if the pointer released over no tracked target.
     * The caller decides what to do with the (source, target) pair, including whether it's valid
     * at all. */
    onFinish: (sourceId: string, targetId: string | null, start: ConnectDragPoint, end: ConnectDragPoint) => void;
}

export interface UseConnectDragResult {
    /** True from `start()` until the drag resolves, via mouseup or `cancel()`. */
    active: boolean;
    sourceId: string | null;
    targetId: string | null;
    startPoint: ConnectDragPoint | null;
    currentPoint: ConnectDragPoint | null;
    /** Begins a connect-drag from `sourceId`, resolving its canvas-space origin from the
     * mousedown's screen coordinates. No-ops if `screenToCanvas` can't resolve a point (e.g. the
     * host canvas isn't mounted yet). */
    start: (sourceId: string, screenX: number, screenY: number) => void;
    /** Marks `id` as the currently-hovered drop target (or null to clear it) — call this from the
     * host's own per-item mouseenter/mouseleave handlers while `active` is true. */
    setHoverTarget: (id: string | null) => void;
    /** Aborts the drag without firing `onFinish` — e.g. on a background click. */
    cancel: () => void;
}

export default function useConnectDrag({ screenToCanvas, onFinish }: UseConnectDragOptions): UseConnectDragResult {
    const [active, setActive] = useState(false);
    const [sourceId, setSourceId] = useState<string | null>(null);
    const [targetId, setTargetId] = useState<string | null>(null);
    const [startPoint, setStartPoint] = useState<ConnectDragPoint | null>(null);
    const [currentPoint, setCurrentPoint] = useState<ConnectDragPoint | null>(null);

    // Refs mirror the state above so the window-level mousemove/mouseup closures (registered once
    // per active drag, see the effect below) always read the latest values instead of a stale
    // snapshot captured when the listener was attached.
    const sourceIdRef = useRef<string | null>(null);
    const targetIdRef = useRef<string | null>(null);
    const startPointRef = useRef<ConnectDragPoint | null>(null);
    const currentPointRef = useRef<ConnectDragPoint | null>(null);
    // Latest callback props, read from inside the listener effect without needing it to
    // re-subscribe (and so re-run its own start/end logic) every time a caller's inline
    // screenToCanvas/onFinish closure is recreated on render.
    const screenToCanvasRef = useRef(screenToCanvas);
    const onFinishRef = useRef(onFinish);
    useEffect(() => { screenToCanvasRef.current = screenToCanvas; }, [screenToCanvas]);
    useEffect(() => { onFinishRef.current = onFinish; }, [onFinish]);

    const reset = useCallback(() => {
        setActive(false);
        setSourceId(null);
        setTargetId(null);
        setStartPoint(null);
        setCurrentPoint(null);
        sourceIdRef.current = null;
        targetIdRef.current = null;
        startPointRef.current = null;
        currentPointRef.current = null;
    }, []);

    const start = useCallback((id: string, screenX: number, screenY: number) => {
        const point = screenToCanvasRef.current(screenX, screenY);
        if (!point) return;
        setActive(true);
        setSourceId(id);
        setTargetId(null);
        setStartPoint(point);
        setCurrentPoint(point);
        sourceIdRef.current = id;
        targetIdRef.current = null;
        startPointRef.current = point;
        currentPointRef.current = point;
    }, []);

    const setHoverTarget = useCallback((id: string | null) => {
        targetIdRef.current = id;
        setTargetId(id);
    }, []);

    const cancel = useCallback(() => reset(), [reset]);

    useEffect(() => {
        if (!active) return;

        const onMove = (e: globalThis.MouseEvent) => {
            const point = screenToCanvasRef.current(e.clientX, e.clientY);
            if (!point) return;
            currentPointRef.current = point;
            setCurrentPoint(point);
        };

        const onUp = () => {
            const source = sourceIdRef.current;
            const startedAt = startPointRef.current;
            const endedAt = currentPointRef.current;
            const target = targetIdRef.current;
            reset();
            if (source && startedAt && endedAt) {
                onFinishRef.current(source, target, startedAt, endedAt);
            }
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [active, reset]);

    return { active, sourceId, targetId, startPoint, currentPoint, start, setHoverTarget, cancel };
}
